import { useId, useState } from 'react';
import { Button } from 'components/common';
import EyeIcon from './EyeIcon.jsx';

/**
 * .input-sm, optionally wrapped in a .form-group with a label.
 *
 * `type="password"` gets the show/hide eye from the login form — it was written
 * once there with imperative innerHTML swapping and is worth having everywhere.
 *
 * `error` renders the message below the field and wires up aria-invalid /
 * aria-describedby, so a screen reader announces the problem rather than the
 * message being a red string only a sighted user finds.
 */
export default function Input({
  label,
  type = 'text',
  className = '',
  wrapperClassName = '',
  error,
  id,
  ...rest
}) {
  const [revealed, setRevealed] = useState(false);
  const autoId = useId();
  const fieldId = id ?? autoId;
  const errorId = `${fieldId}-error`;

  const isPassword = type === 'password';
  const effectiveType = isPassword && revealed ? 'text' : type;

  const field = (
    <input
      id={fieldId}
      type={effectiveType}
      className={['input-sm', error ? 'input-sm--invalid' : '', className].filter(Boolean).join(' ')}
      aria-invalid={error ? true : undefined}
      aria-describedby={error ? errorId : undefined}
      {...rest}
    />
  );

  const control = !isPassword ? field : (
    <div className="pw-field">
      {field}
      <Button
        variant="reveal"
        onClick={() => setRevealed(v => !v)}
        aria-label={revealed ? 'Hide password' : 'Show password'}
        title={revealed ? 'Hide password' : 'Show password'}
      >
        <EyeIcon off={revealed} />
      </Button>
    </div>
  );

  // No label and no error — the caller is placing this inside its own layout.
  if (!label && !error) return control;

  return (
    <div className={['form-group', wrapperClassName].filter(Boolean).join(' ')}>
      {label && <label htmlFor={fieldId}>{label}</label>}
      {control}
      {error && <div className="field-error" id={errorId} role="alert">{error}</div>}
    </div>
  );
}
